import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { faLock, faPaperPlane, faPaperclip, faFileArrowDown, faXmark } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { isCompletoTier } from "../utils/planFeatures";
import { hasUnreadRestaurantChat } from "../utils/chatNotifications";
import { getWebsocketBaseURL } from "../utils/websocketUrl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import { CalendarSvgIcon, PhoneSvgIcon, UsersSvgIcon, UserSvgIcon, ClockSvgIcon, BubbleTextSvgIcon, ZoneSvgIcon, NoPhoneSvgIcon, NoPhoneTwoLinesSvgIcon } from "../icons/SvgIcons";
import { CustomModal } from "./CustomModal";
import { DefaultDateWeekday, DefaultDateSmallMins } from "./DefaultImports";
const StyledInput = styled.input`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  &:focus {
    outline: none;
  }
`;
const ChatButton = styled.button`
  position: relative;
  border: 0;
  background: ${({
  isClient
}) => isClient ? "#EE754C" : "none"};
  color: ${({
  $blocked,
  isClient
}) => $blocked ? "#EE754C" : isClient ? "#FFF" : "#99b0ad"};
  cursor: pointer;
  height: 40px;
  margin: 0;
  padding: ${({
  isClient
}) => isClient ? "0 12px" : "0"};
  border-radius: 5px;
  font-weight: ${({
  isClient
}) => isClient ? "bold" : "normal"};
  font-size: ${({
  isClient
}) => isClient ? "14px" : "inherit"};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-direction: row;
  box-shadow: ${({
  isClient
}) => isClient ? "0 2px 4px rgba(0,0,0,0.2)" : "none"};
  width: ${({
  isClient
}) => isClient ? "100%" : "auto"};
`;
const ChatLockedIcon = styled.span`
  position: absolute;
  top: 0;
  right: -2px;
  color: #ee754c;
  font-size: 13px;
  line-height: 1;
  filter: drop-shadow(0 1px 1px rgba(255, 255, 255, 0.85));
`;
const UpgradeModalText = styled.p`
  margin: 0;
  color: #335c67;
`;
const UpgradeModalButton = styled.button`
  border: 0;
  border-radius: 6px;
  padding: 8px 14px;
  background: ${({
  $secondary
}) => $secondary ? "#99b0ad" : "#ee754c"};
  color: #fff;
  font-weight: 600;
`;
const NotificationIndicator = styled.div`
  position: absolute;
  top: 5px;
  right: 2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  /* The app orange on a white ring, so it stands off the icon and off a white
     card. It only appears for unread messages: the "read" dot it used to share
     this spot with was cream (#FAF6E9) - invisible on white - and anything
     other than orange there reads as a notification that is not one. */
  background-color: #EE754C;
  box-shadow: 0 0 0 2px #ffffff, 0 1px 3px rgba(0, 0, 0, 0.25);
`;
const UpgradeModalHighlight = styled.span`
  color: #ee754c;
  font-weight: 700;
`;
function ReservationChat({
  reservation,
  isClient = false,
  setChatModalTrueOrFalse = false,
  openModal = false,
  hideButton = false,
  triggerIcon = null
}) {
  const {
    t
  } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showChatUpgradeModal, setShowChatUpgradeModal] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  /* Uploaded as soon as it is picked, then held here until the message is
     sent - so the send is still one websocket frame carrying only text. */
  const [attachment, setAttachment] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const socket = useRef(null);
  const navigate = useNavigate();
  const userLogin = useSelector(state => state.userLogin);
  const {
    userInfo
  } = userLogin;
  const chatEndRef = useRef(null);
  const restaurant_info = useSelector(state => state.restaurantDetails);
  const {
    restaurants
  } = restaurant_info;
  const isChatBlocked = !isClient && !isCompletoTier(restaurants?.sub_lvl);
  useEffect(() => {
    if (openModal === true) {
      if (isChatBlocked) {
        setShowChatUpgradeModal(true);
      } else {
        setShowModal(openModal);
      }
    }
  }, [openModal, isChatBlocked]);
  const handleChatButtonClick = () => {
    if (isChatBlocked) {
      setShowChatUpgradeModal(true);
      return;
    }
    setShowModal(true);
  };
  useEffect(() => {
    if (!showModal) return;
    const sender = userInfo ? userInfo.name : reservation.full_name;
    const websocketBaseURL = getWebsocketBaseURL();
    socket.current = new WebSocket(`${websocketBaseURL}chat/init/${reservation.token}/${sender}/${isClient ? "client" : "restaurant"}/`);
    socket.current.onmessage = event => {
      const data = JSON.parse(event.data);
      if (data.type === "chat_history") {
        const sortedMessages = [...data.messages].sort((a, b) => {
          const dateA = new Date(a.timestamp);
          const dateB = new Date(b.timestamp);
          return dateA - dateB;
        });
        setChatHistory(sortedMessages);
      } else {
        setChatHistory(prevHistory => {
          const updatedHistory = [...prevHistory, data];
          return updatedHistory.sort((a, b) => {
            const dateA = new Date(a.timestamp);
            const dateB = new Date(b.timestamp);
            return dateA - dateB;
          });
        });
      }
    };
    return () => socket.current?.close();
  }, [userInfo, showModal, reservation, isClient]);
  const sendMessage = () => {
    if (!socket.current) return;
    if (!newMessage.trim() && !attachment) return;

    socket.current.send(JSON.stringify({
      message: newMessage,
      sender: userInfo ? userInfo.name : reservation.full_name,
      client_or_restaurant: isClient ? "client" : "restaurant",
      attachment_url: attachment?.url || "",
      attachment_name: attachment?.name || "",
      attachment_type: attachment?.type || ""
    }));
    setNewMessage("");
    setAttachment(null);
  };
  const handleFilePicked = async event => {
    const file = event.target.files?.[0];
    // Let the same file be picked again after it has been removed.
    event.target.value = "";
    if (!file) return;

    setAttachmentError("");
    setIsUploading(true);

    try {
      const body = new FormData();
      body.append("file", file);

      const response = await fetch(`/api/reservations/chat/${reservation.token}/attachment/`, {
        method: "POST",
        body
      });

      // A 404 or a 500 comes back as an HTML page, and parsing that as JSON
      // throws - which used to surface as the same "could not send" message as
      // a rejected file type, hiding which of the two had happened.
      const raw = await response.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch (parseError) {
        data = null;
      }

      if (!response.ok) {
        setAttachmentError(data?.detail || `${t("ui.components.reservationChat.attachmentFailed")} (${response.status})`);
        return;
      }

      if (!data?.url) {
        setAttachmentError(`${t("ui.components.reservationChat.attachmentFailed")} (${response.status})`);
        return;
      }

      setAttachment(data);
    } catch (error) {
      setAttachmentError(t("ui.components.reservationChat.attachmentFailed"));
    } finally {
      setIsUploading(false);
    }
  };
  const isUserMessage = message => {
    const viewerSide = isClient ? "client" : "restaurant";
    if (["client", "restaurant"].includes(message?.client_or_restaurant)) {
      return message.client_or_restaurant === viewerSide;
    }

    // Backwards-compatible fallback for old chat rows created before the
    // sender side was stored explicitly.
    const senderName = message?.sender;
    if (isClient) return senderName === reservation.full_name;
    return senderName !== reservation.full_name;
  };
  const closeModal = () => {
    if (setChatModalTrueOrFalse) {
      setChatModalTrueOrFalse(false);
    }
    setShowModal(false);
  };
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      block: "end"
    });
  }, [chatHistory, showModal]);
  const notificationIsActive = isClient ? Boolean(reservation.chat_notification_client) : hasUnreadRestaurantChat(reservation, userInfo);
  return <>
      {!hideButton && <ChatButton isClient={isClient} $blocked={isChatBlocked} onClick={handleChatButtonClick} aria-disabled={isChatBlocked}>
          {isClient && t("ui.components.reservationChat.messages")}

          {triggerIcon || <BubbleTextSvgIcon width={isClient ? 30 : 43} height={isClient ? 30 : 43} strokeColor={isChatBlocked ? "#EE754C" : "#FAF6E9"} />}

          {notificationIsActive && <NotificationIndicator />}
        </ChatButton>}

      <ChatModal show={showModal} onHide={closeModal} centered title={<TitleWrapper>
            <IconGroup>
              <UserSvgIcon width={20} height={20} strokeColor="#99b0ad" />
              <Truncate>{reservation.full_name}</Truncate>
            </IconGroup>

            <IconGroup>
              <CalendarSvgIcon width={20} height={20} strokeColor="#99b0ad" />
              <DefaultDateWeekday date={reservation.date} />
            </IconGroup>

            <IconGroup>
              <ClockSvgIcon width={20} height={20} strokeColor="#99b0ad" />
              {reservation.time}
            </IconGroup>

            <IconGroup>
              <UsersSvgIcon width={24} height={24} strokeColor="#99b0ad" />
              {reservation.number_of_people}
            </IconGroup>

            <IconGroup>
              <ZoneSvgIcon width={20} height={20} strokeColor="#99b0ad" />
              {reservation.table_place_preference ? reservation.table_place_preference.place_of_table : t("ui.i18nCleanup2.naoDefinirSala")}
            </IconGroup>
          </TitleWrapper>} footer={<FooterStack>
            {(attachment || attachmentError || isUploading) && <PendingRow>
                {isUploading && <PendingNote>{t("ui.components.reservationChat.attachmentUploading")}</PendingNote>}

                {attachmentError && <PendingError>{attachmentError}</PendingError>}

                {attachment && !isUploading && <PendingChip>
                    <FontAwesomeIcon icon={faPaperclip} />
                    <PendingName>{attachment.name}</PendingName>
                    <RemoveAttachment type="button" onClick={() => setAttachment(null)} aria-label="remove">
                      <FontAwesomeIcon icon={faXmark} />
                    </RemoveAttachment>
                  </PendingChip>}
              </PendingRow>}

            <InputContainer>
              <HiddenFileInput ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleFilePicked} />

              <AttachButton type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading} title={t("ui.components.reservationChat.attachFile")} aria-label={t("ui.components.reservationChat.attachFile")}>
                <FontAwesomeIcon icon={faPaperclip} size="lg" color="#99b0ad" />
              </AttachButton>

              <StyledTextarea placeholder={t("ui.components.reservationChat.escreverMensagem")} value={newMessage} onChange={e => setNewMessage(e.target.value)} rows={1} />

              <SendButton onClick={sendMessage}>
                <FontAwesomeIcon icon={faPaperPlane} size="lg" color="#9FBBC1" />
              </SendButton>
            </InputContainer>
          </FooterStack>}>
        <ChatBody>
          <ChatHistoryContainer>
            {chatHistory.map((msg, idx) => <React.Fragment key={idx}>
                <SenderName isUserMessage={isUserMessage(msg)}>
                  <span className="sender">{msg.sender}</span>
                </SenderName>

                <TimestampCenter>
                  <span>
                    {msg.timestamp ? <DefaultDateSmallMins date={msg.timestamp} /> : t("ui.i18nFinalCleanup.todayLower")}
                  </span>
                </TimestampCenter>

                <MessageBubble className={isUserMessage(msg) ? "user-message" : "other-message"}>
                  {msg.attachment_url && (String(msg.attachment_type || "").startsWith("image/") ? <AttachmentImage src={msg.attachment_url} alt={msg.attachment_name || ""} onClick={() => window.open(msg.attachment_url, "_blank", "noopener")} /> : <AttachmentLink href={msg.attachment_url} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faFileArrowDown} />
                        <span>{msg.attachment_name || t("ui.components.reservationChat.attachment")}</span>
                      </AttachmentLink>)}

                  {msg.message && msg.message.split("\n").map((line, i) => <React.Fragment key={i}>
                      {line}
                      <br />
                    </React.Fragment>)}
                </MessageBubble>
              </React.Fragment>)}
            <div ref={chatEndRef} />
          </ChatHistoryContainer>
        </ChatBody>
      </ChatModal>

      <CustomModal show={showChatUpgradeModal} onHide={() => setShowChatUpgradeModal(false)} centered title={t("ui.components.reservationChat.chatSoup")} footer={<>
            <UpgradeModalButton type="button" $secondary onClick={() => setShowChatUpgradeModal(false)}>{t("ui.components.reservationChat.fechar")}</UpgradeModalButton>
            <UpgradeModalButton type="button" onClick={() => {
        setShowChatUpgradeModal(false);
        navigate(`/pick_plan_and_pay/${restaurants?.id}`);
      }}>{t("ui.components.reservationChat.verPlanos")}</UpgradeModalButton>
          </>}>
        <UpgradeModalText>{t("ui.components.reservationChat.oChatSoupEstaDisponivelNo")}{" "}
          <UpgradeModalHighlight>{t("ui.components.reservationChat.completo")}</UpgradeModalHighlight>.
        </UpgradeModalText>
      </CustomModal>
    </>;
}
/* Memoised: the reservation list renders every row on any change to the
   selection, so a row whose booking is unchanged must not pay for it. */
export default React.memo(ReservationChat);
const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 12px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  /* The header is white. This was cream, which is why nothing in it could be
     read - and it beats the title colour, being set on the text itself. */
  color: #4f5f5b;
  margin-right: 36px;

  @media (max-width: 576px) {
    font-size: 11.5px;
    gap: 3px 8px;
    margin-right: 28px;
  }
`;
const IconGroup = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;

  /* Sized to the words they sit beside: at 20 and 24px against 13px text the
     row looked like two different components stacked together. */
  svg {
    flex: 0 0 auto;
    width: 15px !important;
    height: 15px !important;

    @media (max-width: 576px) {
      width: 13px !important;
      height: 13px !important;
    }
  }
`;
const Truncate = styled.span`
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 576px) {
    max-width: 70px;
  }
`;
const ChatModal = styled(CustomModal)`
  .modal-dialog {
    max-width: 500px;
    width: 90%;
    min-height: unset;
  }

  .modal-content {
    height: 700px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    flex: 0 0 auto;

    @media (max-width: 576px) {
      padding: 6px 12px;
    }
  }

  /* This row was drawn for a coloured header: cream icons and the modal's
     orange title on white, neither of which could be read on it. It uses the
     app's greys instead. The !important is answering one: index.css sets every
     .modal-title to the app orange with !important, which no amount of
     specificity can outrank. */
  .modal-header .modal-title {
    color: #4f5f5b !important;
    font-weight: 600;
  }

  .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .modal-footer {
    flex: 0 0 auto;
    display: block;
    padding: 0;
  }

  @media (max-width: 576px) {
    .modal-dialog {
      width: 95%;
      margin: 0 auto;
    }

    .modal-content {
      height: 80vh;
    }
  }
`;
const ChatBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;
const ChatHistoryContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
`;
const MessageBubble = styled.div`
  max-width: 80%;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 20px;
  color: #fff;
  word-break: break-word;

  &.user-message {
    background-color: #ee754c;
    margin-left: auto;
    border-bottom-right-radius: 0;
  }

  &.other-message {
    background-color: #99b0ad;
    margin-right: auto;
    border-bottom-left-radius: 0;
  }
`;
const SenderName = styled.div`
  margin-bottom: 5px;
  text-align: ${({
  isUserMessage
}) => isUserMessage ? "right" : "left"};
  color: #6c757d;
  font-size: 0.9rem;
`;
const TimestampCenter = styled.div`
  text-align: center;
  font-size: 0.8rem;
  margin-bottom: 5px;
  color: #6c757d;
`;
const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
`;
const PendingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 0;
`;
const PendingChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 5px 10px;
  border-radius: 999px;
  background: #f4f7f6;
  color: #4f5f5b;
  font-size: 12.5px;
  font-weight: 600;
`;
const PendingName = styled.span`
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const RemoveAttachment = styled.button`
  border: 0;
  padding: 0;
  background: none;
  color: #99b0ad;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #d94b4b;
  }
`;
const PendingNote = styled.span`
  font-size: 12.5px;
  color: #99b0ad;
`;
const PendingError = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: #d94b4b;
`;
const HiddenFileInput = styled.input`
  display: none;
`;
const AttachButton = styled.button`
  flex: 0 0 auto;
  border: 0;
  background: none;
  padding: 0 4px 0 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
const AttachmentImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 220px;
  border-radius: 10px;
  margin-bottom: 6px;
  cursor: pointer;
`;
const AttachmentLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  overflow-wrap: anywhere;
`;
const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  border-top: 1px solid #ced4da;
  background: #fff;
`;
const StyledTextarea = styled.textarea`
  flex-grow: 1;
  border: none;
  padding: 0.375rem 0.75rem;
  resize: none;
  max-height: 120px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &:focus {
    outline: none;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f4f4f4;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 10px;
  }
`;
const SendButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 0 15px;
  flex: 0 0 auto;
`;
